// src/hooks/useMediaQuery.js
//
// Shared breakpoint hook for SHARC's mobile responsive rollout.
// See: SHARC_Mobile_Responsive_Spec_v1.1.docx, Section 4.2
//
// Returns a boolean that tracks whether the given media query currently
// matches, and updates automatically on resize / orientation change.
// Pages consume this to conditionally merge mobile-override style
// fragments (Smobile) into their existing inline style objects (S) at
// render time — see Section 4.3 of the spec for the usage pattern.

import { useState, useEffect } from 'react';

export function useMediaQuery(query) {
  const getMatches = () => {
    // Guard for any non-browser render context (tests, SSR, etc.)
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);

    // Keep state in sync with the query string itself changing.
    setMatches(mql.matches);

    // addEventListener is standard, but older Safari (pre-14) only
    // supports the deprecated addListener/removeListener pair.
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    } else {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
  }, [query]);

  return matches;
}

// ── Breakpoint definitions (Spec Section 4.1) ───────────────────────────────
// Mobile:  <= 767px
// Tablet:  768px - 1023px
// Desktop: >= 1024px

export const useIsMobile = () => useMediaQuery('(max-width: 767px)');

export const useIsTablet = () =>
  useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');
